import { useEffect } from 'react';
import { C } from '../constants';
import Glass from '../components/Glass';
import { setMeta } from '../utils/meta';

const accent = C.cyan;

const FAQS = [
  {
    q: 'What Is UnofficialElections?',
    a: 'UnofficialElections.com is a crowd-sourced straw poll for Indian elections. It gives ordinary citizens a fast, zero-friction way to express their political preference and compare their district with the rest of the state. The platform is not affiliated with any government body or political party.',
  },
  {
    q: 'Why Does This Platform Exist?',
    a: 'India is the world\'s largest democracy, but during election season there is a gap: no simple, trustworthy, freely accessible way for ordinary citizens to see how their neighbourhood compares to the rest of the state. Official polls are expensive and controlled by media houses. Social media polls are noisy and manipulated. UnofficialElections fills that gap with a clean, fast, anonymous straw poll that takes ten seconds to complete.',
  },
  {
    q: 'What Is UnofficialElections NOT?',
    a: 'Not an official poll — no affiliation with the Election Commission of India, any government body, or any political party. Not a scientific survey — the data is crowd-sourced and should not be treated as a prediction. Not political — no party, candidate, or ideology is endorsed. Not a data collection tool — no tracking, no profiling, no selling of data.',
  },
  {
    q: 'How Does Voting Work?',
    a: 'Visit the site and pick a state. Select your preferred party. Your vote is recorded anonymously and saved locally on your device so you can only vote once per state. See live results: district maps, statewide charts, and individual district breakdowns, all updating in real time.',
  },
  {
    q: 'How Does the Platform Handle Accuracy and Manipulation?',
    a: 'UnofficialElections is a sentiment poll, not a scientific survey. It uses device-based vote limiting (one vote per device per state) to reduce duplicate voting. No registration, phone number, or email is required. All results are displayed openly with no opaque weighting, no hidden methodology, and no editorial filter.',
  },
  {
    q: 'Which States Are Currently Live?',
    a: 'Tamil Nadu (38 districts, 10 parties) and West Bengal (23 districts, 6 parties) are live for the 2026 assembly elections.',
  },
  {
    q: 'Which States Are Coming Next?',
    a: 'Seven states vote in 2027 — Uttar Pradesh (75 districts), Punjab (23), Gujarat (33), Goa (2), Uttarakhand (13), Himachal Pradesh (12), and Manipur (16). All will be added to the platform.',
  },
  {
    q: 'What Is the Vision Behind UnofficialElections?',
    a: 'Every Indian state election on one platform, with district-level data for every voter, zero barriers, and a living map of how India votes — built by the people, for the people.',
  },
  {
    q: 'How Can I Get in Touch?',
    a: 'Follow @unofficialelections on Instagram for updates, feedback, or collaboration opportunities.',
  },
];

const STATES_TABLE = [
  { state: 'Uttar Pradesh', districts: 75, year: '2027' },
  { state: 'Punjab',        districts: 23, year: '2027' },
  { state: 'Gujarat',       districts: 33, year: '2027' },
  { state: 'Goa',           districts: 2,  year: '2027' },
  { state: 'Uttarakhand',   districts: 13, year: '2027' },
  { state: 'Himachal Pradesh', districts: 12, year: '2027' },
  { state: 'Manipur',       districts: 16, year: '2027' },
];

export default function AboutScreen() {
  useEffect(() => {
    setMeta({
      title:       "About UnofficialElections - India's Free Anonymous Election Platform",
      description: "UnofficialElections is India's free, anonymous election polling platform. Learn how it works, what data we collect (none), and our vision for covering every Indian election.",
      ogImage:     '/og-home.png',
      canonical:   '/about',
    });
  }, []);
  return (
    <main className="px-5 md:px-10 py-10 max-w-4xl mx-auto">
      <div style={{ fontSize: '0.7rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: accent, fontWeight: 600 }}>
        About
      </div>
      <h1 style={{
        fontWeight: 700, fontSize: 'clamp(1.8rem, 4.5vw, 3rem)',
        lineHeight: 1.05, letterSpacing: '-0.025em', marginTop: '0.4rem', color: C.white,
      }}>
        India's Free Anonymous
        <br />
        <span style={{
          background: `linear-gradient(90deg, ${accent}, ${C.pink})`,
          WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent', fontStyle: 'italic',
        }}>
          Election Polling Platform.
        </span>
      </h1>

      <p style={{ fontSize: '1rem', color: C.textDim, lineHeight: 1.7, marginTop: '1.2rem', maxWidth: 660 }}>
        UnofficialElections.com is India's free, anonymous election polling platform. Voters pick a state, cast an informal vote for their preferred party, and instantly see live district-level results on an interactive map — without sharing any personal data.
      </p>

      <div style={{ borderTop: `1px solid ${C.border}`, marginTop: '2.5rem', marginBottom: '2.5rem' }} />

      {/* FAQ pairs */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {FAQS.map((item, i) => (
          <div key={i}>
            <h2 style={{ fontWeight: 700, fontSize: '1.1rem', color: C.white, marginBottom: '0.5rem', letterSpacing: '-0.01em' }}>
              {item.q}
            </h2>
            <p style={{ fontSize: '0.92rem', color: 'rgba(255,255,255,0.72)', lineHeight: 1.75, margin: 0 }}>
              {item.a}
            </p>
          </div>
        ))}
      </div>

      {/* Upcoming states table */}
      <div style={{ marginTop: '3rem' }}>
        <h2 style={{ fontWeight: 700, fontSize: '1.1rem', color: C.white, marginBottom: '1rem' }}>
          States Coming in 2027
        </h2>
        <Glass style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
              <thead>
                <tr style={{ background: 'rgba(255,255,255,0.04)', borderBottom: `1px solid ${C.border}` }}>
                  {['State', 'Districts', 'Year'].map(h => (
                    <th key={h} style={{ padding: '10px 16px', textAlign: 'left', color: C.textDim, fontWeight: 600, fontSize: '0.72rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {STATES_TABLE.map((row, i) => (
                  <tr key={i} style={{ borderTop: i > 0 ? `1px solid ${C.border}` : 'none' }}>
                    <td style={{ padding: '10px 16px', color: C.white, fontWeight: 600 }}>{row.state}</td>
                    <td style={{ padding: '10px 16px', color: C.textDim }}>{row.districts}</td>
                    <td style={{ padding: '10px 16px' }}>
                      <span style={{ color: accent, fontWeight: 600, fontSize: '0.8rem', background: 'rgba(0,229,255,0.08)', padding: '2px 10px', borderRadius: 999 }}>
                        {row.year}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Glass>
      </div>

      <Glass style={{ marginTop: '2.5rem', padding: '1.1rem 1.3rem' }}>
        <p style={{ fontSize: '0.92rem', color: C.white, margin: 0, fontStyle: 'italic', lineHeight: 1.65 }}>
          Disclaimer: UnofficialElections.com is an entertainment and civic engagement project. All data is crowd-sourced and unofficial. Results do not represent scientific polling, official election results, or predictions. The platform is non-partisan and non-political.
        </p>
      </Glass>
    </main>
  );
}
